import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import moment from "moment";
import DynamicFormField, { isShowTravelDynamicField } from "./DynamicFormField";
import InsuranceService from "@src/services/InsuranceService";
import { IDENTITY_PROOF_MIN_SIZE_BYTES } from "@src/config/constant";

const ordinalLabel = (index: number) => {
    if (index === 0) return "Primary Traveler Details";
    const number = index + 1;
    const suffix = number % 10 === 1 && number % 100 !== 11 ? "st" : number % 10 === 2 && number % 100 !== 12 ? "nd" : number % 10 === 3 && number % 100 !== 13 ? "rd" : "th";
    return `${number}${suffix} Traveler Details`;
};

const getGroupKey = (label: string, index: number) => (
    label
        ? label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")
        : `traveller_group_${index}`
);

const getTravellerGroups = (formSection: any) => {
    const blockFields = formSection?.blocks?.flatMap((blockItem: any) => blockItem?.fields || []) || [];
    if (formSection?.blocks?.length && !blockFields.some((fieldItem: any) => fieldItem.type === "text")) {
        return formSection.blocks;
    }

    const fields = formSection?.fields || blockFields;
    const groups: any[] = [];
    let currentGroup: any = null;

    fields.forEach((fieldItem: any, index: number) => {
        if (fieldItem.type === "text") {
            currentGroup = {
                key: getGroupKey(fieldItem.label, index),
                title: fieldItem.label,
                fields: [],
            };
            groups.push(currentGroup);
            return;
        }

        if (!currentGroup) {
            currentGroup = {
                key: "traveller_details",
                title: formSection?.title || "Traveller Details",
                fields: [],
            };
            groups.push(currentGroup);
        }

        currentGroup.fields.push(fieldItem);
    });

    return groups;
};

const isEmergencyContactGroup = (group: any) => (
    String(group?.key || group?.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").includes("emergency_contact_details")
);

const isAddressDetailsGroup = (group: any) => (
    String(group?.key || group?.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").includes("address_details")
);

const isPrimaryTraveller = (travellerIndex: number) => travellerIndex === 0;

const sameAddressFieldNames = ["province", "city", "district", "barangay", "street_address", "address"];

const isSameAddressField = (fieldItem: any) => sameAddressFieldNames.includes(String(fieldItem?.base_name || fieldItem?.name || "").toLowerCase());

const getFieldKey = (fieldItem: any) => String(fieldItem?.base_name || fieldItem?.name || "").toLowerCase();
const isProvinceField = (fieldItem: any) => getFieldKey(fieldItem) === "province";
const isCityField = (fieldItem: any) => getFieldKey(fieldItem) === "city";
const isLocalityField = (fieldItem: any) => ["district", "barangay"].includes(getFieldKey(fieldItem));
const isMobileNumberField = (fieldItem: any) => ["mobile_number", "mobile", "emergency_number", "emer_mobile", "emer_number"].includes(fieldItem?.base_name);
const isRelationshipTextField = (fieldItem: any) => ["relationship", "emer_relationship", "emergency_relationship"].includes(fieldItem?.base_name);
const MAX_MOBILE_NUMBER_LENGTH = 10;
const getRawFieldValue = (value: any) => value?.value ?? value;

const travellerFieldAliases: Record<string, string[]> = {
    date_of_birth: ["date_of_birth", "dob"],
    dob: ["dob", "date_of_birth"],
    mobile_number: ["mobile_number", "mobile"],
    mobile: ["mobile", "mobile_number"],
    street_address: ["street_address", "address"],
    address: ["address", "street_address"],
    district: ["district", "barangay"],
    barangay: ["barangay", "district"],
    emer_mobile: ["emer_mobile", "emer_number", "emergency_number"],
    emer_number: ["emer_number", "emer_mobile", "emergency_number"],
    passport_expiry_date: ["passport_expiry_date", "passport_expiry"],
    passport_expiry: ["passport_expiry", "passport_expiry_date"],
};

const getTravellerApiValue = (traveller: any, fieldName: string) => {
    const names = travellerFieldAliases[fieldName] || [fieldName];
    const matchedName = names.find((name) => traveller?.[name] !== undefined && traveller?.[name] !== null);
    return matchedName ? traveller[matchedName] : undefined;
};

const normalizeAddressValue = (value: any) => {
    const rawValue = value?.value ?? value;
    return rawValue === undefined || rawValue === null ? "" : String(rawValue).trim();
};

const isSameTravellerAddress = (travellerData: any, primaryTravellerData: any) => {
    const addressFields = ["province", "city", "barangay", "street_address"];
    if (!travellerData || !primaryTravellerData) return false;

    return addressFields.every((fieldName) => {
        const travellerValue = normalizeAddressValue(getTravellerApiValue(travellerData, fieldName));
        const primaryValue = normalizeAddressValue(getTravellerApiValue(primaryTravellerData, fieldName));
        return travellerValue !== "" && primaryValue !== "" && travellerValue === primaryValue;
    });
};

const getEmergencyContactFields = (fields: any[] = []) => {
    const fieldOrder = [
        "emer_suffix",
        "emer_first_name",
        "emer_middle_name",
        "emer_last_name",
        "emer_mobile",
        "emer_relationship",
        "emer_travel_purpose",
    ];

    return [...fields].sort((firstField: any, secondField: any) => {
        const firstIndex = fieldOrder.indexOf(firstField.base_name || firstField.name);
        const secondIndex = fieldOrder.indexOf(secondField.base_name || secondField.name);
        return (firstIndex === -1 ? fieldOrder.length : firstIndex) - (secondIndex === -1 ? fieldOrder.length : secondIndex);
    });
};

const travellerDetailsOptionConfigKeys: Record<string, string> = {
    suffix: "suffix",
    emer_suffix: "suffix",
    civil_status: "civil_status",
    gender: "gender",
    relationship_with: "travel_traveller",
    travel_purpose: "travel_purpose",
    emer_travel_purpose: "travel_purpose",
};

const passportDocumentKeys: Record<number, string> = {
    1: "passport_front",
    2: "passport_back",
};

const getPassportDocuments = (documents: any[] = []) => documents.reduce((documentMap: any, documentItem: any) => {
    const documentKey = passportDocumentKeys[Number(documentItem?.doc_id || documentItem?.document_id)];
    if (documentKey) documentMap[documentKey] = documentItem;
    return documentMap;
}, {});

const getConfigOptions = (config: any, key: string) => (
    Array.isArray(config?.[key])
        ? config[key].filter((option: any) => option?.value !== undefined && option?.value !== null && option?.label !== undefined && option?.label !== null)
        : []
);

const isDobField = (field: any) => {
    const fieldKey = String(field?.base_name || field?.name || "").toLowerCase();
    const fieldLabel = String(field?.label || "").toLowerCase();
    return ["dob", "date_of_birth"].includes(fieldKey) || fieldLabel.includes("date of birth") || fieldLabel === "dob";
};

const isPassportExpiryField = (field: any) => ["passport_expiry", "passport_expiry_date"].includes(getFieldKey(field));
const getPassportExpiryMinDate = () => moment().add(1, "day").startOf("day").toDate();
const isDateField = (field: any) => isDobField(field) || isPassportExpiryField(field);
const isFieldVisibleOnWeb = (field: any) => field?.is_show === true && field?.for_web === true;
const getPrefilledDateValue = (field: any, value: any) => {
    const dateValue = moment(value);
    if (!value || !dateValue.isValid()) return "";
    if (isPassportExpiryField(field) && dateValue.isBefore(moment("1900-01-01"), "day")) return "";
    return dateValue.toDate();
};

const createField = (field: any, travellerIndex: number, currentField?: any, commonConfig?: any, travellerDobValue?: any, provinceOptions: any[] = [], primaryName: string = "") => {
    const configKey = travellerDetailsOptionConfigKeys[field.name];
    const fieldKey = String(field.name || "").toLowerCase();
    const dobField = isDobField(field);

    const obj: Record<string, any> = {
        ...JSON.parse(JSON.stringify(field)),
        id: `${travellerIndex}-${field.name}`,
        base_name: field.name,
        name: `${field.name}_${travellerIndex}`,
        field_show: field.field_show,
        child_depedencies: field.child_depedencies,
        is_enable: dobField ? false : field.is_enable !== false,
        is_show: field.name === "relationship_with" && isPrimaryTraveller(travellerIndex) ? false : isFieldVisibleOnWeb(field),
        minDate: isPassportExpiryField(field) ? getPassportExpiryMinDate() : currentField?.minDate ?? field.minDate,
        value: dobField ? travellerDobValue || currentField?.value || field.value || "" : currentField?.value ?? field.value ?? "",
        error_msg: field.error_msg || `${field.label} is required`,
        error_message: "",
        options: configKey
            ? getConfigOptions(commonConfig, configKey)
            : fieldKey === "province"
                ? provinceOptions
                : currentField?.options || field.options || [],
    };
    
    const isPrimaryFirstNameField = travellerIndex === 0 && String(field?.name || "").toLowerCase() === "first_name";
    if (isPrimaryFirstNameField) {
        if (String(primaryName || "").trim()) obj.value = primaryName;
        obj.is_enable = false;
    }
    return obj;
};

const createTraveller = (travellerIndex: number, groupsConfig: any[], currentTraveller?: any, commonConfig?: any, travellerDobValues: any[] = [], provinceOptions: any[] = [], primaryName: string = "") => ({
    person_id: currentTraveller?.person_id,
    is_same_address_as_primary: (!isPrimaryTraveller(travellerIndex) && currentTraveller?.is_same_address_as_primary) || false,
    groups: groupsConfig.map((group) => ({
        ...JSON.parse(JSON.stringify(group)),
        fields: (group.fields || []).map((field: any) => {
            const currentField = currentTraveller?.groups
                ?.flatMap((groupItem: any) => groupItem.fields || [])
                ?.find((fieldItem: any) => fieldItem.base_name === field.name || fieldItem.name === field.name);
            return createField(field, travellerIndex, currentField, commonConfig, travellerDobValues[travellerIndex], provinceOptions, primaryName);
        }),
    })),
    documents: {
        uploaded: currentTraveller?.documents?.uploaded || [],
        passport_front: currentTraveller?.documents?.passport_front || null,
        passport_back: currentTraveller?.documents?.passport_back || null,
    },
});

const createPrefilledTraveller = (travellerIndex: number, groupsConfig: any[], travellerData: any, commonConfig?: any, travellerDobValues: any[] = [], provinceOptions: any[] = [], primaryTravellerData?: any) => {
    const primaryName = travellerIndex === 0 ? String(travellerData?.primary_first_name || "") : "";
    const traveller = createTraveller(travellerIndex, groupsConfig, undefined, commonConfig, travellerDobValues, provinceOptions, primaryName);
    const passportDocuments = getPassportDocuments(travellerData?.documents || travellerData?.uploaded || []);
    const isSameAddressAsPrimary = !isPrimaryTraveller(travellerIndex) && (
        !!travellerData?.is_same_address_as_primary || isSameTravellerAddress(travellerData, primaryTravellerData)
    );

    return {
        ...traveller,
        person_id: travellerData?.person_id || travellerData?.id,
        is_same_address_as_primary: isSameAddressAsPrimary,
        groups: traveller.groups.map((group: any) => ({
            ...group,
            fields: group.fields.map((fieldItem: any) => {
                const fieldKey = String(fieldItem.base_name || fieldItem.name || "").toLowerCase();
                const resolvedApiValue = fieldKey === "first_name"
                    ? getTravellerApiValue(travellerData, "first_name")
                    : getTravellerApiValue(travellerData, fieldItem.base_name || fieldItem.name);
                if (resolvedApiValue === undefined) return fieldItem;

                const isPrefilledPrimaryFirstName = isPrimaryTraveller(travellerIndex)
                    && (fieldKey === "first_name" || fieldKey === "primary_first_name");

                return {
                    ...fieldItem,
                    value: isDateField(fieldItem) ? getPrefilledDateValue(fieldItem, resolvedApiValue) : resolvedApiValue,
                    is_enable: isPrefilledPrimaryFirstName ? false : fieldItem.is_enable,
                };
            }),
        })),
        documents: {
            uploaded: travellerData?.documents || travellerData?.uploaded || [],
            passport_front: travellerData?.passport_front || passportDocuments.passport_front || null,
            passport_back: travellerData?.passport_back || passportDocuments.passport_back || null,
        },
    };
};

const getDocumentUrl = (documentItem: any) => {
    if (!documentItem) return "";
    if (typeof documentItem === "string") return documentItem;
    if (documentItem.url || documentItem.src || documentItem.file_url || documentItem.doc_path) {
        return documentItem.url || documentItem.src || documentItem.file_url || documentItem.doc_path;
    }
    if (documentItem instanceof File) return URL.createObjectURL(documentItem);
    return "";
};

const getDocumentName = (documentItem: any) => {
    if (!documentItem) return "";
    if (typeof documentItem === "string") return documentItem.split("?")[0].split("/").pop() || "Uploaded Document";
    const documentUrl = getDocumentUrl(documentItem);
    if (!documentItem.name && documentUrl) return documentUrl.split("?")[0].split("/").pop() || "Uploaded Document";
    return documentItem.name || documentItem.file_name || "Uploaded Document";
};

const isImageDocument = (documentItem: any) => {
    const name = getDocumentName(documentItem).toLowerCase();
    const documentUrl = getDocumentUrl(documentItem).split("?")[0].toLowerCase();
    const type = documentItem?.type || "";
    return type.includes("image") || ["jpg", "jpeg", "png", "gif", "webp"].some((extension) => (
        name.endsWith(`.${extension}`) || documentUrl.endsWith(`.${extension}`)
    ));
};

const getDocumentFileType = (documentItem: any) => {
    const type = documentItem?.type || "";
    if (type.includes("pdf")) return "pdf";

    const name = getDocumentName(documentItem).split("?")[0].toLowerCase();
    const extension = name.match(/\.([a-z0-9]+)$/)?.[1];

    return extension || "pdf";
};

const isEmptyField = (fieldItem: any) => {
    const value = fieldItem?.value;
    return value === "" || value === null || value === undefined || (typeof value === "object" && !value.value && !(value instanceof Date));
};

const validateField = (fieldItem: any) => {
    if (fieldItem.is_required && isEmptyField(fieldItem)) {
        return fieldItem.error_msg;
    }

    const value = fieldItem?.value?.value ?? fieldItem?.value;
    if (!value) return "";

    if (["email_address", "email"].includes(fieldItem.base_name) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) {
        return "Please enter a valid email address";
    }

    if (["mobile_number", "mobile", "emergency_number", "emer_mobile", "emer_number"].includes(fieldItem.base_name) && !/^[9]\d{9}$/.test(String(value))) {
        return "Mobile number should start with 9 and 10 digit number";
    }

    if (isRelationshipTextField(fieldItem) && !/^[A-Za-z\s]+$/.test(String(value))) {
        return "Relationship should contain only alphabetical values";
    }

    if (["passport_no", "passport_number", "passport"].includes(fieldItem.base_name) && !/^[A-Z0-9]+$/.test(String(value))) {
        return "Passport Number should contain only uppercase alphanumeric values";
    }

    if (["nationality"].includes(fieldItem.base_name) && !/^[A-Za-z\s]+$/.test(String(value))) {
        return "Nationality should contain only alphabetical values";
    }

    if (["passport_expiry_date", "passport_expiry"].includes(fieldItem.base_name) && !moment(value).isAfter(moment(), "day")) {
        return "Passport Expiry Date must be a future date";
    }

    return "";
};

const sanitizeTravellerField = (fieldItem: any) => {
    if (isMobileNumberField(fieldItem)) {
        return { ...fieldItem, value: String(fieldItem.value || "").replace(/\D/g, "").slice(0, MAX_MOBILE_NUMBER_LENGTH) };
    }

    if (isRelationshipTextField(fieldItem)) {
        return { ...fieldItem, value: String(fieldItem.value || "").replace(/[^A-Za-z\s]/g, "") };
    }

    if (["passport_no", "passport_number", "passport"].includes(fieldItem.base_name)) {
        return { ...fieldItem, value: String(fieldItem.value || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase() };
    }

    if (["nationality"].includes(fieldItem.base_name)) {
        return { ...fieldItem, value: String(fieldItem.value || "").replace(/[^A-Za-z\s]/g, "") };
    }

    return fieldItem;
};

const getLiveFieldError = (fieldItem: any) => validateField(fieldItem);

const getLiveValidatedField = (fieldItem: any) => {
    const sanitizedField = sanitizeTravellerField(fieldItem);
    return {
        ...sanitizedField,
        error_message: getLiveFieldError(sanitizedField),
    };
};

const normalizeOptions = (response: any) => (
    Array.isArray(response?.data?.data)
        ? response.data.data.filter((option: any) => option?.value !== undefined && option?.value !== null && option?.label)
        : []
);

type TravellersDetailsProps = {
    travellersCount: number;
    travellerDobValues?: any[];
    formSection?: any;
    travelCommonConfig?: any;
    prefilledTravellers?: any[];
    prefillKey?: string | number;
    onTravellersChange?: (travellers: any[]) => void;
    primaryName?: string;
};

const TravellersDetails = forwardRef((props: TravellersDetailsProps, ref: any) => {
    const travellerGroups = useMemo(() => getTravellerGroups(props.formSection), [props.formSection]);
    const [provinceOptions, setProvinceOptions] = useState<any[]>([]);
    const [travellers, setTravellers] = useState<any[]>(() => [createTraveller(0, travellerGroups, undefined, props.travelCommonConfig, props.travellerDobValues, provinceOptions, props.primaryName || "")]);
    const [expandedSections, setExpandedSections] = useState<number[]>([0]);
    const [appliedPrefillKey, setAppliedPrefillKey] = useState<string | number | undefined>();
    const [fileInputResetKey, setFileInputResetKey] = useState<Record<string, number>>({});

    const count = useMemo(() => Math.max(Number(props.travellersCount) || 1, 1), [props.travellersCount]);

    const updateTravellers = (updater: (currentTravellers: any[]) => any[]) => {
        setTravellers((currentTravellers) => {
            const updatedTravellers = updater(currentTravellers);
            props.onTravellersChange?.(updatedTravellers);
            return updatedTravellers;
        });
    };

    const updateAddressOptions = (travellerIndex: number, fieldMatcher: (fieldItem: any) => boolean, options: any[], selectedParent?: { matcher: (fieldItem: any) => boolean; value: any }) => {
        updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => {
            if (index !== travellerIndex) return traveller;

            if (selectedParent) {
                const parentField = traveller.groups
                    ?.flatMap((group: any) => group.fields || [])
                    ?.find(selectedParent.matcher);
                if (String(getRawFieldValue(parentField?.value)) !== String(selectedParent.value)) return traveller;
            }

            return {
                ...traveller,
                groups: traveller.groups.map((group: any) => ({
                    ...group,
                    fields: group.fields.map((fieldItem: any) => (
                        fieldMatcher(fieldItem)
                            ? { ...fieldItem, options }
                            : fieldItem
                    )),
                })),
            };
        }));
    };

    const fetchProvinceOptions = async () => {
        try {
            const response = await InsuranceService.getStateList();
            const options = normalizeOptions(response);
            setProvinceOptions(options);
            updateTravellers((currentTravellers) => currentTravellers.map((traveller) => ({
                ...traveller,
                groups: traveller.groups.map((group: any) => ({
                    ...group,
                    fields: group.fields.map((fieldItem: any) => (
                        isProvinceField(fieldItem)
                            ? { ...fieldItem, options }
                            : fieldItem
                    )),
                })),
            })));
        } catch (error) {
            setProvinceOptions([]);
        }
    };

    const fetchCityOptions = async (provinceValue: any, travellerIndex: number) => {
        if (!provinceValue) return;

        try {
            const response = await InsuranceService.getCityList({ state: [provinceValue] });
            updateAddressOptions(travellerIndex, isCityField, normalizeOptions(response), { matcher: isProvinceField, value: provinceValue });
        } catch (error) {
            updateAddressOptions(travellerIndex, isCityField, [], { matcher: isProvinceField, value: provinceValue });
        }
    };

    const fetchLocalityOptions = async (cityValue: any, travellerIndex: number) => {
        if (!cityValue) return;

        try {
            const response = await InsuranceService.getLocalityList({ city: [cityValue] });
            updateAddressOptions(travellerIndex, isLocalityField, normalizeOptions(response), { matcher: isCityField, value: cityValue });
        } catch (error) {
            updateAddressOptions(travellerIndex, isLocalityField, [], { matcher: isCityField, value: cityValue });
        }
    };

    const handleAddressFieldChange = (travellerIndex: number, fieldItem: any) => {
        const fieldValue = getRawFieldValue(fieldItem.value);
        if (!isProvinceField(fieldItem) && !isCityField(fieldItem) && !isLocalityField(fieldItem)) return;

        updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => {
            if (index !== travellerIndex) return traveller;

            return {
                ...traveller,
                groups: traveller.groups.map((group: any) => ({
                    ...group,
                    fields: group.fields.map((currentField: any) => {
                        if (currentField.name === fieldItem.name) {
                            return { ...currentField, value: fieldItem.value, error_message: fieldItem.error_message };
                        }

                        if (isProvinceField(fieldItem) && (isCityField(currentField) || isLocalityField(currentField))) {
                            return { ...currentField, value: "", error_message: "", options: [] };
                        }

                        if (isCityField(fieldItem) && isLocalityField(currentField)) {
                            return { ...currentField, value: "", error_message: "", options: [] };
                        }

                        return currentField;
                    }),
                })),
            };
        }));

        if (isProvinceField(fieldItem)) {
            fetchCityOptions(fieldValue, travellerIndex);
        } else if (isCityField(fieldItem)) {
            fetchLocalityOptions(fieldValue, travellerIndex);
        }
    };

    const handleTravellerFieldChange = (travellerIndex: number, fieldItem: any) => {
        const validatedField = getLiveValidatedField(fieldItem);
        fieldItem.value = validatedField.value;
        fieldItem.error_message = validatedField.error_message;

        handleAddressFieldChange(travellerIndex, validatedField);
    };

    const validateTravellers = () => {
        let isValid = true;
        const invalidIndexes: number[] = [];
        const updatedTravellers = travellers.map((traveller, travellerIndex) => {
            let travellerHasError = false;
            const groups = traveller.groups.map((group: any) => ({
                ...group,
                fields: group.fields.map((fieldItem: any) => {
                    if (!isPrimaryTraveller(travellerIndex) && isEmergencyContactGroup(group)) {
                        return { ...fieldItem, error_message: "" };
                    }

                    if (!isPrimaryTraveller(travellerIndex) && traveller.is_same_address_as_primary && isAddressDetailsGroup(group) && isSameAddressField(fieldItem)) {
                        return { ...fieldItem, error_message: "" };
                    }

                    const isVisible = isShowTravelDynamicField(fieldItem, traveller.groups);
                    const documentValue = traveller.documents?.[fieldItem.base_name || fieldItem.name];
                    const errorMessage = isVisible && fieldItem.type === "image"
                        ? fieldItem.is_required && !documentValue ? fieldItem.error_msg : ""
                        : isVisible ? validateField(fieldItem) : "";
                    if (errorMessage) {
                        isValid = false;
                        travellerHasError = true;
                    }
                    return { ...fieldItem, error_message: errorMessage };
                }),
            }));

            if (travellerHasError) invalidIndexes.push(travellerIndex);
            return { ...traveller, groups };
        });

        setTravellers(updatedTravellers);
        props.onTravellersChange?.(updatedTravellers);

        if (invalidIndexes.length) {
            setExpandedSections((current) => Array.from(new Set([...current, ...invalidIndexes])));
        }

        return isValid;
    };

    useImperativeHandle(ref, () => ({
        validate: validateTravellers,
        getTravellers: () => travellers,
    }));

    useEffect(() => {
        updateTravellers((currentTravellers) => {
            const nextTravellers = [];
            for (let index = 0; index < count; index += 1) {
                nextTravellers.push(createTraveller(index, travellerGroups, currentTravellers[index], props.travelCommonConfig, props.travellerDobValues, provinceOptions));
            }
            return nextTravellers;
        });

        setExpandedSections((current) => {
            const visibleIndexes = current.filter((index) => index < count);
            return visibleIndexes.length ? visibleIndexes : [0];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, travellerGroups, props.travelCommonConfig, props.travellerDobValues, provinceOptions]);

    useEffect(() => {
        if (!props.prefillKey || appliedPrefillKey === props.prefillKey || !props.prefilledTravellers?.length || !travellerGroups.length) return;

        const prefilledCount = Math.max(count, props.prefilledTravellers.length);
        const primaryTravellerData = props.prefilledTravellers[0] || {};
        const nextTravellers = Array.from({ length: prefilledCount }, (_item, index) => (
            createPrefilledTraveller(index, travellerGroups, props.prefilledTravellers?.[index] || {}, props.travelCommonConfig, props.travellerDobValues, provinceOptions, primaryTravellerData)
        ));

        updateTravellers(() => nextTravellers);
        setExpandedSections([0]);
        setAppliedPrefillKey(props.prefillKey);

        props.prefilledTravellers.forEach((travellerData: any, index: number) => {
            const provinceValue = getTravellerApiValue(travellerData, "province");
            const cityValue = getTravellerApiValue(travellerData, "city");
            if (provinceValue) fetchCityOptions(provinceValue, index);
            if (cityValue) fetchLocalityOptions(cityValue, index);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.prefillKey, props.prefilledTravellers, travellerGroups, props.travelCommonConfig, props.travellerDobValues, provinceOptions]);

    useEffect(() => {
        fetchProvinceOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleSection = (index: number) => {
        setExpandedSections((current) => (
            current.includes(index)
                ? current.filter((item) => item !== index)
                : [...current, index]
        ));
    };

    const handleSameAddressToggle = (travellerIndex: number) => {
        updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => (
            index === travellerIndex
                ? { ...traveller, is_same_address_as_primary: !traveller.is_same_address_as_primary }
                : traveller
        )));
    };

    const handleFileChange = (travellerIndex: number, key: string, file: File | null) => {
        if (file && (file.type === "image/tiff" || /\.(tif|tiff)$/i.test(file.name))) {
            setFileInputResetKey((current) => {
                const mapKey = `${travellerIndex}-${key}`;
                return { ...current, [mapKey]: (current[mapKey] || 0) + 1 };
            });

            updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => (
                index === travellerIndex
                    ? {
                        ...traveller,
                        documents: { ...traveller.documents, [key]: null },
                        groups: traveller.groups.map((group: any) => ({
                            ...group,
                            fields: group.fields.map((fieldItem: any) => (
                                (fieldItem.base_name || fieldItem.name) === key
                                    ? { ...fieldItem, error_message: "TIF files are not allowed" }
                                    : fieldItem
                            )),
                        })),
                    }
                    : traveller
            )));
            return;
        }

        if (file && file.size < IDENTITY_PROOF_MIN_SIZE_BYTES) {
            setFileInputResetKey((current) => {
                const mapKey = `${travellerIndex}-${key}`;
                return { ...current, [mapKey]: (current[mapKey] || 0) + 1 };
            });

            updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => (
                index === travellerIndex
                    ? {
                        ...traveller,
                        documents: { ...traveller.documents, [key]: null },
                        groups: traveller.groups.map((group: any) => ({
                            ...group,
                            fields: group.fields.map((fieldItem: any) => (
                                (fieldItem.base_name || fieldItem.name) === key
                                    ? { ...fieldItem, error_message: "File size should be at least 25 KB" }
                                    : fieldItem
                            )),
                        })),
                    }
                    : traveller
            )));
            return;
        }

        updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => (
            index === travellerIndex
                ? {
                    ...traveller,
                    documents: { ...traveller.documents, [key]: file },
                    groups: traveller.groups.map((group: any) => ({
                        ...group,
                        fields: group.fields.map((fieldItem: any) => (
                            (fieldItem.base_name || fieldItem.name) === key
                                ? { ...fieldItem, error_message: "" }
                                : fieldItem
                        )),
                    })),
                }
                : traveller
        )));
    };

    const removeUploadedDocument = (travellerIndex: number, key: string, fileIndex?: number) => {
        updateTravellers((currentTravellers) => currentTravellers.map((traveller, index) => (
            index === travellerIndex
                ? {
                    ...traveller,
                    documents: {
                        ...traveller.documents,
                        [key]: Array.isArray(traveller.documents[key])
                            ? traveller.documents[key].filter((_: any, index: number) => index !== fileIndex)
                            : null,
                    },
                }
                : traveller
        )));
    };

    const renderDocumentPreview = (documentItem: any, onRemove: () => void, label?: string) => {
        const documentUrl = getDocumentUrl(documentItem);
        const documentName = getDocumentName(documentItem);
        const fileType = getDocumentFileType(documentItem);

        return (
            <>
                <div role="presentation" className="upload-image-proof">
                    <span className="close-icn" onClick={onRemove}>
                        <i className="ic-clearclose"></i>
                    </span>
                    {isImageDocument(documentItem) && documentUrl ? (
                        <img src={documentUrl} alt={documentName} />
                    ) : (
                        <a href={documentUrl} target="_blank" rel="noreferrer">
                            <i className={`ic-${fileType}-file f-50`}></i>
                        </a>
                    )}
                </div>
            </>
        );
    };

    const renderUploadBox = (travellerIndex: number, fieldItem: any, documentItem: any, disabled = false) => (
        <div className="col-md-4 col-xs-12 p-lg-t travel-proof-item" key={fieldItem.base_name}>
            {documentItem ? (
                renderDocumentPreview(documentItem, () => removeUploadedDocument(travellerIndex, fieldItem.base_name), fieldItem.label)
            ) : (
                <>
                    <div role="presentation" className="add-document-proof">
                        <input
                            key={`${travellerIndex}-${fieldItem.base_name}-${fileInputResetKey[`${travellerIndex}-${fieldItem.base_name}`] || 0}`}
                            type="file"
                            accept="image/*,.pdf"
                            name={`${fieldItem.base_name}_${travellerIndex}`}
                            disabled={disabled}
                            onChange={(event) => handleFileChange(travellerIndex, fieldItem.base_name, event.target.files?.[0] || null)}
                        />
                        <i className="ic-add"></i>
                        <span className="f-16">{fieldItem.label}</span>
                    </div>
                </>
            )}
            {fieldItem.error_message ? <span className="error-txt">{fieldItem.error_message}</span> : null}
        </div>
    );

    const renderSameAddressToggle = (traveller: any, travellerIndex: number) => {
        if (isPrimaryTraveller(travellerIndex)) return null;

        return (
            <label className="travel-same-address-toggle">
                <span>Is your address is same as the Primary Traveller address?</span>
                <input
                    type="checkbox"
                    checked={!!traveller.is_same_address_as_primary}
                    onChange={() => handleSameAddressToggle(travellerIndex)}
                />
                <span className="travel-same-address-switch" />
            </label>
        );
    };

    const getTravellerFields = (traveller: any) => traveller?.groups?.flatMap((groupItem: any) => groupItem.fields || []) || [];

    const getPrimaryAddressField = (fieldItem: any) => {
        const fieldKey = getFieldKey(fieldItem);
        const aliases = travellerFieldAliases[fieldKey] || [fieldKey];

        return getTravellerFields(travellers[0]).find((primaryField: any) => {
            const primaryFieldKey = getFieldKey(primaryField);
            const primaryAliases = travellerFieldAliases[primaryFieldKey] || [primaryFieldKey];
            return aliases.includes(primaryFieldKey) || primaryAliases.includes(fieldKey);
        });
    };

    const getGroupDisplayFields = (traveller: any, travellerIndex: number, group: any) => {
        const fields = group.fields.filter((fieldItem: any) => fieldItem.type !== "image");
        const shouldShowPrimaryAddress = !isPrimaryTraveller(travellerIndex) && traveller.is_same_address_as_primary && isAddressDetailsGroup(group);

        if (!shouldShowPrimaryAddress) return fields;

        return fields.map((fieldItem: any) => {
            if (!isSameAddressField(fieldItem)) return fieldItem;

            const primaryField = getPrimaryAddressField(fieldItem);
            return {
                ...fieldItem,
                value: primaryField?.value ?? fieldItem.value,
                options: primaryField?.options || fieldItem.options,
                error_message: "",
                is_enable: false,
            };
        });
    };

    return (
        <div className="travel-travellers-details">
            <h2 className="heading-h2">Travellers Details</h2>

            {travellers.map((traveller, travellerIndex) => {
                const isExpanded = expandedSections.includes(travellerIndex);
                return (
                    <div className="travel-traveller-accordion" key={`traveller-${travellerIndex}`}>
                        <button type="button" className="travel-accordion-head" onClick={() => toggleSection(travellerIndex)}>
                            <span>{ordinalLabel(travellerIndex)}</span>
                            <span className="travel-accordion-line" />
                            <span className="travel-accordion-icon">{isExpanded ? "-" : "+"}</span>
                        </button>

                        {isExpanded && (
                            <div className="travel-accordion-body">
                                {traveller.groups
                                    .filter((group: any) => isPrimaryTraveller(travellerIndex) || !isEmergencyContactGroup(group))
                                    .map((group: any) => (
                                        <div className="travel-traveller-group" key={`${travellerIndex}-${group.key}`}>
                                            <h3 className="f-16">{group.title}</h3>
                                            {isAddressDetailsGroup(group) ? renderSameAddressToggle(traveller, travellerIndex) : null}
                                            {isEmergencyContactGroup(group) ? (
                                                <>
                                                    <div className="row">
                                                        <DynamicFormField
                                                            fields={getEmergencyContactFields(group.fields).filter((fieldItem: any) => !["image", "radio"].includes(fieldItem.type))}
                                                            blocks={traveller.groups}
                                                            onValueChange={(fieldItem: any) => handleTravellerFieldChange(travellerIndex, fieldItem)}
                                                        />
                                                    </div>
                                                    <div className="row">
                                                        <DynamicFormField
                                                            fields={getEmergencyContactFields(group.fields).filter((fieldItem: any) => fieldItem.type === "radio")}
                                                            blocks={traveller.groups}
                                                            rowClass="col-md-12"
                                                            onValueChange={(fieldItem: any) => handleTravellerFieldChange(travellerIndex, fieldItem)}
                                                        />
                                                    </div>
                                                </>
                                            ) : group.key === "travel_purpose" ? (
                                                <div className="row">
                                                    <DynamicFormField
                                                        fields={group.fields.filter((fieldItem: any) => fieldItem.type !== "image")}
                                                        blocks={traveller.groups}
                                                        rowClass="col-md-12"
                                                        onValueChange={(fieldItem: any) => handleTravellerFieldChange(travellerIndex, fieldItem)}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="row">
                                                    <DynamicFormField
                                                        fields={getGroupDisplayFields(traveller, travellerIndex, group)}
                                                        blocks={traveller.groups}
                                                        onValueChange={(fieldItem: any) => handleTravellerFieldChange(travellerIndex, fieldItem)}
                                                    />
                                                </div>
                                            )}

                                            {String(group.key || group.title).toLowerCase().includes("identity") && (
                                                <div className="travel-identity-proofs">
                                                    <div className="upload-heading-text">
                                                        <h3 className="f-16">Identity Proofs(Optional)</h3>
                                                    </div>
                                                    <div className="row travel-proof-list">
                                                        {group.fields
                                                            .filter((fieldItem: any) => fieldItem.type === "image" && isFieldVisibleOnWeb(fieldItem))
                                                            .map((fieldItem: any) => renderUploadBox(
                                                                travellerIndex,
                                                                fieldItem,
                                                                traveller.documents[fieldItem.base_name],
                                                                fieldItem.is_enable === false
                                                            ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

export default TravellersDetails;
